//
// File: ShellComponent.tsx
// Description: A react component that loads a remote module containing
// a react component, then renders it as a child.
// Copyright: Broadlume 2024
//

import * as React from 'react';
import { IShellPluginProps } from './IShellPluginProps';
import {
  IShellComponentProps,
  IShellComponentState,
  IDuplicateRequestHandlers,
} from './ShellComponent.types';
import { IShellPluginConfig } from './IShellPluginConfig';

import {
  loadRemoteModule,
  isContainerLoaded,
  isModuleLoaded,
  getModule,
  loadRemoteCss,
  deactivateRemotePluginCss,
} from './PluginLoader';

/**
 * The ShellComponent is responsible for loading the remote component and rendering it.
 * It takes a config object that defines the remote component to load and also takes
 * care of exposing a shared context to the remote component.
 */
export class ShellComponent extends React.Component<IShellComponentProps, IShellComponentState> {
  // override the context/Type properties to ensure that the context is typed correctly for ShellContext
  //public static override contextType: React.Context<unknown> = ShellContext;
  //public override context!: React.ContextType<typeof ShellContext>;

  /**
   * Keeps track of same script being requested multiple times at once
   */
  private duplicateRequestHandlers: IDuplicateRequestHandlers = {};

  /**
   * Override state to use IShellComponentState
   */
  public override state: IShellComponentState = {
    Component: null,
    hasError: false,
  };

  /**
   * Override componentDidMount which loads the remote component
   * defined in the config
   * @returns A promise that resolves when the component is loaded
   */
  public override async componentDidMount(): Promise<void> {
    let error = null;
    const component = await this.loadComponent().catch((e) => (error = e));

    this.setState({
      Component: component,
      hasError: error !== null,
    });
  }

  /**
   * Override componentDidUpdate which reloads the remote component if it's config changes
   * @param prevProps - The previous props
   */
  public override async componentDidUpdate(prevProps: IShellComponentProps): Promise<void> {
    const { config } = prevProps;

    if (this.shouldReload(config)) {
      let error = null;
      const component = await this.loadComponent().catch((e) => (error = e));
      this.setState({
        Component: component,
        hasError: error !== null,
      });
    }
  }

  /**
   * Disable this plugin's stylesheets when unmounting so they do not
   * cascade over the next federated remote.
   */
  public override componentWillUnmount(): void {
    deactivateRemotePluginCss(this.props.config);
  }

  /**
   * Override componentDidCatch to forward the error to the onError prop
   * and set the hasError state to true
   * @param error - The error that was thrown
   * @param errorInfo - The error info
   */
  public override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { onError } = this.props;
    if (onError) onError(error, errorInfo);

    this.setState({ hasError: true });
  }

  /**
   * Override render to forward the props plus the config to the remote component
   * @returns The remote component on exists, otherwise a placeholder component
   * defined in the props. If an error occues returns an error component
   */
  public override render(): React.ReactElement | null {
    const { renderError, renderPlaceholder, config, ...otherProps } = this.props;
    const { Component, hasError } = this.state;

    if (hasError) {
      return renderError ? renderError() : null;
    }
    if (!Component) {
      return renderPlaceholder ? renderPlaceholder() : null;
    }

    return <Component config={config} {...otherProps} />;
  }

  /**
   * Checks if the config has changed based on the previous config
   * name, src or module
   * @param prevConfig - The previous config
   * @returns True if the config has changed, otherwise false
   */
  private shouldReload(prevConfig: IShellPluginConfig): boolean {
    const { config } = this.props;
    const reload =
      prevConfig.name !== config.name ||
      prevConfig.src !== config.src ||
      prevConfig.module !== config.module;
    return reload;
  }

  // loadComponent loads the remote component defined in the props config
  /**
   * Loads the remote component defined in the props config.
   * Also handles multiple requests for the same script at once by tracking
   * inflight requests and resolving/rejecting them when the script is loaded.
   * @returns A promise resolving to the remote component
   */
  private async loadComponent(): Promise<React.ComponentType<IShellPluginProps>> {
    return new Promise(
      (
        resolve: (componentType: React.ComponentType<IShellPluginProps>) => void,
        reject: (error: Error) => void,
      ): void => {
        const { onLoad, onLoaded, onError, config } = this.props;
        if (!this.isValidComponentConfig(config)) {
          reject(new Error('Invalid configuration'));
          return;
        }

        if (onLoad) onLoad(config);

        const fileName = `${config.src}:${config.module}`;
        if (this.isComponentLoaded(config)) {
          // Cached remount: activate CSS so inactive plugin styles stay disabled
          loadRemoteCss(config)
            .catch((error) => {
              console.error('Error loading remote css', error);
            });
          if (onLoaded) onLoaded(config);
          return resolve(this.getComponentTemplate(config));
        }

        // do we have an in-flight request for this script?
        if (Object.prototype.hasOwnProperty.call(this.duplicateRequestHandlers, fileName)) {
          // if so add this request to the list of handlers and return
          this.duplicateRequestHandlers[fileName].push({
            resolve: (componentType: React.ComponentType<IShellPluginProps>) => {
              if (onLoaded) onLoaded(config);
              resolve(componentType);
            },
            reject: (error: Error) => {
              if (onError) onError(error);
              reject(error);
            },
          });

          return;
        }

        // create a new array for handlers for this script
        this.duplicateRequestHandlers[fileName] = this.duplicateRequestHandlers[fileName] || [];
        // load the module defined in the config
        loadRemoteModule(config)
          .then((module: any) => {
            if (this.isComponentLoaded(config)) {
              const template = this.getComponentTemplate(config);

              //Need to initialize Css if found
              loadRemoteCss(config).catch((error) => {
                console.error('Error loading remote css', error);
              });

              if (onLoaded) onLoaded(config);
              resolve(module.default || module);
              // resolve(module.default || module);

              this.duplicateRequestHandlers[fileName].forEach(({ resolve: success }) =>
                success(template),
              );
            } else {
              const error = new Error('Script was loaded, but component was not found.');

              if (onError) onError(error);
              reject(error);
              this.duplicateRequestHandlers[fileName].forEach(({ reject: fail }) => fail(error));
            }

            delete this.duplicateRequestHandlers[fileName];
          })
          .catch((error: Error) => {
            if (onError) onError(error);
            reject(error);
            this.duplicateRequestHandlers[fileName].forEach(({ reject: fail }) => fail(error));
            delete this.duplicateRequestHandlers[fileName];
          });
      },
    );
  }

  /**
   * Checks if the remote component defined in the config is loaded
   * @param config - The config to check
   * @returns True if the component is loaded, otherwise false
   */
  private isComponentLoaded(config: IShellPluginConfig): boolean {
    const loaded = isContainerLoaded(config) && isModuleLoaded(config);
    return loaded;
  }

  /**
   * Gets the remote component defined in the config
   * @param config - The config to get the component for
   * @returns The remote module as a component
   */
  private getComponentTemplate(config: IShellPluginConfig): React.ComponentType<IShellPluginProps> {
    const module = getModule(config);
    // @ts-expect-error check
    const template = (module.default || module) as React.ComponentType<IShellPluginProps>;
    return template;
  }

  /**
   * Checks if the component config is valid i.e.
   * it has a name, src and module
   * @param componentConfig - The component config to check
   * @returns True if the config is valid, otherwise false
   */
  private isValidComponentConfig(componentConfig: IShellPluginConfig): boolean {
    const valid = !!(componentConfig.name && componentConfig.src && componentConfig.module);
    return valid;
  }
}
